import { Injectable } from '@angular/core';
import { ApiBodyData, ApiService } from '@app/gateway';
import { FactoryRepository, IModelFactory } from '../factory';

@Injectable({ providedIn: 'root' })
export abstract class BaseRepository<T> {
  protected readonly model: IModelFactory;

  constructor(
    protected readonly factoryRepository: FactoryRepository,
    protected readonly apiService: ApiService
  ) {
    this.model = factoryRepository.getModel(this.getModelName());
  }

  protected abstract getModelName(): string;

  public async count(data: ApiBodyData): Promise<number> {
    const path: string = `${this.model.getPath()}/count`;
    return this.apiService.post({ path, data });
  }

  public async exists(data: ApiBodyData): Promise<boolean> {
    const path: string = `${this.model.getPath()}/exists`;
    return this.apiService.post({ path, data });
  }

  public async create(data: T): Promise<T> {
    const path: string = `${this.model.getPath()}/create`;
    return this.apiService.post({ path, data: data as ApiBodyData });
  }

  public async upsert(data: T): Promise<T> {
    const path: string = `${this.model.getPath()}/upsert`;
    return this.apiService.post({ path, data: data as ApiBodyData });
  }

  public async update(id: string | number, data: T): Promise<T> {
    const path: string = `${this.model.getPath()}/update/${id}`;
    return this.apiService.patch({ path, data: data as ApiBodyData });
  }

  public async replace(id: string | number, data: T): Promise<T> {
    const path: string = `${this.model.getPath()}/replace/${id}`;
    return this.apiService.put({ path, data: data as ApiBodyData });
  }

  public async delete(id: string | number): Promise<boolean> {
    const path: string = `${this.model.getPath()}/delete/${id}`;
    return this.apiService.delete({ path });
  }

  public async find(filter: ApiBodyData): Promise<T[]> {
    const path: string = `${this.model.getPath()}/find`;
    return this.apiService.post({ path, data: filter });
  }

  public async findOne(filter: ApiBodyData): Promise<T> {
    const path: string = `${this.model.getPath()}/findOne`;
    return this.apiService.post({ path, data: filter });
  }

  public async findById(id: string | number): Promise<T> {
    const path: string = `${this.model.getPath()}/findById/${id}`;
    return this.apiService.get({ path });
  }
}
